import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Sketch from '@arcgis/core/widgets/Sketch';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';

import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  finalize,
  first,
  from,
  map,
  merge,
  mergeMap,
  Observable,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { FormatterUtils } from 'app/shared/utils/formatter.utils';
import { PinUtils } from 'app/shared/utils/pin.utils';
import { UnitUtils } from 'app/shared/utils/unit.utils';
import { MainMapService } from '../../main-map.service';
import { path } from '../../default-symbols';
import {
  CalcPathService,
  PathResponseLike,
  SearchPathFilterLike,
} from './calc-path.service';
import { ToastService } from 'app/shared/services/toast.service';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { ScreenService } from 'app/shared/services/screen.service';
import Graphic from '@arcgis/core/Graphic';
import { TranslationService } from '@services/translation.service';

interface CalcPathFormLike {
  uniqueEntity: FormControl<boolean>;
  maxDistance: FormControl<number>;
}

interface PinCtrlTplContext {
  pinUrlObservable: Observable<string>;
  placeholder: string;
  pointValueObservable: Observable<string>;
  activeObservable: Observable<boolean>;
  onPinClick?: () => void;
}

const defaultValues = {
  uniqueEntity: false,
  maxDistance: 100,
};

@Component({
  selector: 'app-calc-path',
  templateUrl: 'calc-path.component.html',
  styleUrls: ['calc-path.component.scss'],
  providers: [CalcPathService],
})
export class CalcPathComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  mapView: __esri.MapView;
  @Input()
  groupLayer: __esri.GroupLayer;

  formGroup: FormGroup<CalcPathFormLike>;

  aPinUrlObservable: Observable<string>;
  startPointGraphicSubject = new BehaviorSubject<__esri.Graphic>(null);
  startPointActiveSubject = new BehaviorSubject<boolean>(false);
  aPinCtrlContext: PinCtrlTplContext;

  bPinUrlObservable: Observable<string>;
  endPointGraphicSubject = new BehaviorSubject<__esri.Graphic>(null);
  endPointActiveSubject = new BehaviorSubject<boolean>(false);
  bPinCtrlContext: PinCtrlTplContext;

  yesOrNotList: CodedValueItem<boolean>[] = [
    {
      label: 'Sim',
      value: true,
    },
    {
      label: 'Não',
      value: false,
    },
  ];

  maxDistanceList: CodedValueItem<number>[] = [10, 20, 50, 100].map((value) => {
    const label = `${value}m`;
    return { label, value };
  });

  private pointCaptureSubject = new Subject<{
    imgUrlObservable: Observable<string>;
    pointGraphicSubject: Subject<__esri.Graphic>;
    pointActiveSubject: Subject<boolean>;
  }>();
  private drawPathSubject = new BehaviorSubject<__esri.Graphic>(null);
  private drawPathActiveSubject = new BehaviorSubject<boolean>(false);
  drawPathActiveObservable = this.drawPathActiveSubject.pipe(
    distinctUntilChanged()
  );

  private _pathResultSelectedSubject = new BehaviorSubject<__esri.Graphic>(
    null
  );

  pathList: PathResponseLike[];

  loadingController = new LoadingController();

  private mapViewSubscription: Subscription;
  private searchResultsSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private mainMapService: MainMapService,
    private calcPathService: CalcPathService,
    private toastService: ToastService,
    private screenService: ScreenService,
    private ts: TranslationService
  ) {
    this.formGroup = this.formBuilder.group<CalcPathFormLike>({
      uniqueEntity: this.formBuilder.control<boolean>(null),
      maxDistance: this.formBuilder.control<number>(null),
    });

    this.formGroup.reset(defaultValues);
  }

  ngOnInit(): void {
    const onPinClickFn = (cfg: {
      pointActiveSubject: BehaviorSubject<boolean>;
      imgUrlObservable: Observable<string>;
      pointGraphicSubject: Subject<__esri.Graphic>;
    }) => {
      this.resetPath();

      const active = !cfg.pointActiveSubject.getValue();
      cfg.pointActiveSubject.next(active);
      if (active) {
        this.pointCaptureSubject.next(cfg);
      } else {
        this.pointCaptureSubject.next(null);
      }
    };

    this.aPinUrlObservable = from(PinUtils.createPin('A')).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
    this.aPinCtrlContext = {
      pinUrlObservable: this.aPinUrlObservable,
      placeholder: 'Ponto inicial',
      pointValueObservable: this.startPointGraphicSubject.pipe(
        map((graphic) => {
          return this.pointToStr(graphic?.geometry as any);
        }),
        distinctUntilChanged()
      ),
      activeObservable: this.startPointActiveSubject.pipe(
        distinctUntilChanged()
      ),
      onPinClick: () => {
        onPinClickFn({
          pointActiveSubject: this.startPointActiveSubject,
          imgUrlObservable: this.aPinUrlObservable,
          pointGraphicSubject: this.startPointGraphicSubject,
        });
      },
    };

    this.bPinUrlObservable = from(PinUtils.createPin('B')).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
    this.bPinCtrlContext = {
      pinUrlObservable: this.bPinUrlObservable,
      placeholder: 'Ponto final',
      pointValueObservable: this.endPointGraphicSubject.pipe(
        map((graphic) => {
          return this.pointToStr(graphic?.geometry as any);
        }),
        distinctUntilChanged()
      ),
      activeObservable: this.endPointActiveSubject.pipe(distinctUntilChanged()),
      onPinClick: () => {
        onPinClickFn({
          pointActiveSubject: this.endPointActiveSubject,
          imgUrlObservable: this.bPinUrlObservable,
          pointGraphicSubject: this.endPointGraphicSubject,
        });
      },
    };

    this.drawPathActiveObservable
      .pipe(takeUntil(this.lifecycle))
      .subscribe((active) => {
        if (active) {
          this.resetPoints();
        }
      });

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.reset();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView || changes.groupLayer) {
      this.handleMapView();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.mapViewSubscription?.unsubscribe();

    this.aPinCtrlContext = null;
    this.bPinCtrlContext = null;
  }

  onBtnShowPathClick(item: PathResponseLike) {
    const graphic = new Graphic({
      geometry: item.geometry,
      symbol: path.polylineSymbolDefault,
    });
    this._pathResultSelectedSubject.next(graphic);
  }

  onSubmit(e) {
    e?.preventDefault();
    this.searchResultsSubscription?.unsubscribe();

    if (this.formGroup.invalid) {
      return;
    }

    let filter: SearchPathFilterLike;

    const values = this.formGroup.getRawValue();

    let line = this.drawPathSubject.getValue()?.geometry as __esri.Polyline;
    if (line) {
      filter = { ...values, line };
    } else {
      const startPoint = this.startPointGraphicSubject.getValue()
        ?.geometry as __esri.Point;
      const finalPoint = this.endPointGraphicSubject.getValue()
        ?.geometry as __esri.Point;

      if (startPoint && finalPoint) {
        filter = { ...values, startPoint, finalPoint };
      }
    }

    if (!filter) {
      return;
    }

    this.loadingController.show();
    this.searchResultsSubscription = this.calcPathService
      .searchPaths(filter)
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          this.pathList = list;

          if (!list?.length) {
            this.toastService.warning(
              this.ts.getCurrentLanguage()['no-route-found']
            );
          }
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['unable-calculate-routes']
          );
        },
      });
  }

  onBtnClearClick() {
    this.reset();
  }

  onBtnDrawPathClick() {
    this.resetPoints();
    this.drawPathActiveSubject.next(!this.drawPathActiveSubject.getValue());
  }

  private reset() {
    this.resetPath();
    this.resetPoints();
    this._pathResultSelectedSubject.next(null);

    this.handleMapView();

    this.searchResultsSubscription?.unsubscribe();
    this.pathList = null;

    this.formGroup.reset(defaultValues);
  }

  private resetPath() {
    this.drawPathActiveSubject.next(false);
    this.drawPathSubject.next(null);
  }

  private resetPoints() {
    this.startPointActiveSubject.next(false);
    this.startPointGraphicSubject.next(null);
    this.endPointActiveSubject.next(false);
    this.endPointGraphicSubject.next(null);
  }

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();
    if (!this.mapView) {
      return;
    }

    const subscription = new Subscription();

    const groupLayer: __esri.LayersMixin = this.groupLayer ?? this.mapView.map;

    const drawGraphicLayer = new GraphicsLayer();
    groupLayer.add(drawGraphicLayer);
    const pathResponseGraphicLayer = new GraphicsLayer();
    groupLayer.add(pathResponseGraphicLayer);
    const pathRefGraphicLayer = new GraphicsLayer();
    groupLayer.add(pathRefGraphicLayer);

    const handleGraphicOperatorFn = (graphicsLayer: GraphicsLayer) => {
      return switchMap((graphic: __esri.Graphic) => {
        if (!graphic) {
          return EMPTY;
        }

        return new Observable((subscriber) => {
          graphicsLayer.add(graphic);

          subscriber.add(() => {
            graphicsLayer.remove(graphic);
          });
        });
      });
    };

    subscription.add(
      this.drawPathSubject
        .pipe(handleGraphicOperatorFn(pathRefGraphicLayer))
        .subscribe()
    );
    subscription.add(
      this.startPointGraphicSubject
        .pipe(handleGraphicOperatorFn(pathRefGraphicLayer))
        .subscribe()
    );
    subscription.add(
      this.endPointGraphicSubject
        .pipe(handleGraphicOperatorFn(pathRefGraphicLayer))
        .subscribe()
    );

    subscription.add(
      this._pathResultSelectedSubject
        .pipe(
          tap((graphic) => {
            this.mainMapService.goTo(graphic?.geometry);
          }),
          handleGraphicOperatorFn(pathResponseGraphicLayer)
        )
        .subscribe()
    );

    const sketch = new Sketch({
      view: this.mapView,
      layer: drawGraphicLayer,
      creationMode: 'single',
    });

    subscription.add(
      this.drawPathActiveSubject
        .pipe(
          switchMap((active) => {
            if (!active) {
              return EMPTY;
            }

            return new Observable((subscriber) => {
              this.drawPathSubject.next(null);

              subscriber.add(() => {
                sketch.cancel();

                this.drawPathActiveSubject.next(false);
              });

              subscriber.add(
                EsriUtils.on(sketch, 'create').subscribe(
                  (evt: __esri.SketchCreateEvent) => {
                    switch (evt.state) {
                      case 'cancel':
                        subscriber.complete();
                        break;
                      case 'complete':
                        if (evt.graphic) {
                          this.drawPathSubject.next(evt.graphic);

                          subscriber.complete();
                        }
                        break;
                    }
                  }
                )
              );

              sketch.viewModel.polylineSymbol =
                path.polylineSymbolDrawing as any;
              sketch.create('polyline');
            });
          })
        )
        .subscribe()
    );

    subscription.add(
      this.pointCaptureSubject
        .pipe(
          switchMap((cfg) => {
            if (!cfg) {
              return EMPTY;
            }

            return new Observable((subscriber) => {
              cfg.pointGraphicSubject.next(null);

              subscriber.add(() => {
                sketch.cancel();

                cfg.pointActiveSubject.next(false);
              });

              subscriber.add(
                EsriUtils.on(sketch, 'create').subscribe(
                  (evt: __esri.SketchCreateEvent) => {
                    switch (evt.state) {
                      case 'cancel':
                        subscriber.complete();
                        break;
                      case 'complete':
                        if (evt.graphic) {
                          cfg.pointGraphicSubject.next(evt.graphic);

                          subscriber.complete();
                        }
                        break;
                    }
                  }
                )
              );

              subscriber.add(
                cfg.imgUrlObservable
                  .pipe(
                    first(),
                    mergeMap((imgUrl) => {
                      return new Observable<HTMLImageElement>((subscriber) => {
                        const image = new Image();
                        image.src = imgUrl;
                        image.onload = () => {
                          image.onload = null;
                          subscriber.next(image);
                          subscriber.complete();
                        };
                      });
                    })
                  )
                  .subscribe((image) => {
                    const heightPt = UnitUtils.pxToPt(image.height);
                    const pointSymbol = new PictureMarkerSymbol({
                      url: image.src,
                      width: UnitUtils.pxToPt(image.width),
                      height: heightPt,
                      yoffset: heightPt / 2,
                    });
                    sketch.viewModel.pointSymbol = pointSymbol as any;

                    sketch.create('point');
                  })
              );
            });
          })
        )
        .subscribe()
    );

    subscription.add(() => {
      sketch.cancel();
      sketch.destroy();

      groupLayer.remove(pathRefGraphicLayer);
      pathRefGraphicLayer.destroy();
      groupLayer.remove(drawGraphicLayer);
      drawGraphicLayer.destroy();
    });

    this.mapViewSubscription = subscription;
  }

  private pointToStr(point: __esri.Point) {
    return FormatterUtils.formatPoint(point);
  }
}
